/**
 * HLOS-255 — Composable Vue 3 para gerenciar trigger de LX no ConversationChat.
 *
 * Encapsula a UX flow de disclosure → aceitar LX → executar LX → completar/abandonar.
 * Honra:
 * - CL-07 (vault local-first): state em vault cifrado
 * - CL-08 (Ética fechada): LGPD disclosure antes de qualquer LX; delete UX disponível
 * - CC-05 (zero penalidade): abandonar NÃO afeta θ_lat
 * - Princípio V (Aprendiz Decide): learner pode recusar sem penalidade
 * - Princípio X (Dignidade): zero dark patterns; copy neutra
 * - Princípio XI (Companion ≠ LLM): zero menção a Foundation Models/LLM
 *
 * HLOS-249 → HLOS-254 → HLOS-255:
 * - HLOS-249: Vue renderers + Pinia store (placeholder)
 * - HLOS-254: bridge Pinia ↔ Pyodide real (PR #322)
 * - HLOS-255: integrage UX no ConversationChat.vue (este composable)
 */

import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useLXStore } from '../store'
import type { LXState, LXInteractionEvent } from '../types'

export interface LXDisclosureState {
  /** Disclosure UX para propor nova LX (Princípio V — disclose antes de executar) */
  readonly isProposalVisible: boolean
  /** Disclosure UX para LGPD delete (Art. 18) */
  readonly isLGPDVisible: boolean
  /** true se LX está rodando (currentLX status=in_progress) */
  readonly isLXActive: boolean
}

export function useLXTrigger() {
  const store = useLXStore()
  const { currentLX, currentProposal, isLoading, lastError } = storeToRefs(store)

  const isProposalVisible = computed<boolean>(() => currentProposal.value !== null)
  const isLXActive = computed<boolean>(
    () => currentLX.value !== null && currentLX.value.status === 'in_progress',
  )
  const isLGPDVisible = computed<boolean>(() => Boolean(localStorage.getItem('hlos.lx.disclose_lgpd')))

  /**
   * HLOS-255 — handler para aceitar LX proposta.
   *
   * Princípio V: usuário decide. Honrado — disclosure já foi mostrada (via
   * `isProposalVisible`), learner optou por aceitar.
   *
   * Args:
   *   lxId: identificador canônico da LX (vem da LXProposal).
   *   goal: meta contextual (vem da LXProposal.rationale ou texto livre).
   */
  async function onAcceptLX(lxId: string, goal: string): Promise<void> {
    const proposal = currentProposal.value
    if (!proposal) {
      return  // sem proposta = nada a aceitar (Princípio V Honrado)
    }
    // Marca disclosure como vista (LGPD Art. 18 — opt-in granular)
    localStorage.setItem('hlos.lx.disclose_lgpd', 'true')
    // Limpa proposal + inicia LX via Pinia store (HLOS-254 bridge)
    store.setProposal(null)
    await store.startLX(lxId, goal, proposal.lx_type, proposal.parameters)
  }

  /**
   * HLOS-255 — handler para recusar LX proposta.
   *
   * Princípio V + CC-05: recusa é aprendizado, NÃO penalidade.
   * Zero side-effect em θ_lat — apenas limpa proposal local.
   */
  function onDeclineLX(): void {
    store.setProposal(null)
    // HLOS-253 (Gap D): emitir evento de log (LX_LOG) — futuro HLOS-260
  }

  /**
   * HLOS-255 — handler para interaction do learner com a LX.
   * Chama Pinia store (HLOS-254 bridge → LXEngine.process_interaction).
   */
  async function onLXInteraction(
    instanceId: string,
    event: LXInteractionEvent,
  ): Promise<void> {
    await store.processInteraction(instanceId, event)
  }

  /**
   * HLOS-255 — handler para abandonar LX ativa.
   *
   * CC-05 + Princípio V: NÃO penaliza θ_lat. CompanionRuntime consulta
   * `LXState.abandoned_recently` em R-policy para decidir próxima iniciativa.
   */
  async function onLXAbandon(instanceId: string): Promise<void> {
    await store.abandonLX(instanceId)
  }

  /**
   * HLOS-255 — handler para LX concluída. Limpa state local (UX feedback).
   */
  function onLXComplete(_state: LXState): void {
    store.completeLX()
  }

  /**
   * HLOS-255 — handler LGPD Art. 18: deletar TODAS as LXs do vault cifrado.
   * Honra CL-08 (Ética fechada — learner tem direito ao esquecimento).
   */
  async function onDeleteLXState(): Promise<void> {
    await store.deleteLXState()
  }

  return {
    // Reactive state (refs)
    currentLX,
    currentProposal,
    isLoading,
    lastError,

    // Computed flags
    isProposalVisible,
    isLXActive,
    isLGPDVisible,

    // Handlers
    onAcceptLX,
    onDeclineLX,
    onLXInteraction,
    onLXAbandon,
    onLXComplete,
    onDeleteLXState,
  }
}